import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TrendDownIcon } from '../../../assets/icons/dashboard/decrease-arrow-icon.svg';
import { ReactComponent as TrendUpIcon } from '../../../assets/icons/dashboard/increse-arrow-icon.svg';
import { ReactComponent as RevenueIcon } from '../../../assets/icons/dashboard/revenue-status-icon.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as EnergyIcon } from '../../../assets/icons/dashboard/white-energy.svg';
import { ReactComponent as LoaderIcon } from '../../../assets/icons/loader.svg';
import { ReactComponent as MultiUsersIcon } from '../../../assets/icons/sidebar/sidebar-account-active.svg';
import { ReactComponent as ActiveDevicesIcon } from '../../../assets/icons/sidebar/sidebar-device-active.svg';
import { useAppBranding } from '../../../config/branding';
import { useBranding } from '../../../contexts/BrandingContext';
import MyButton from '../../../shared-components/button/Button';
import { getRelativeTime } from '../../../shared-functions/DateTime';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import dashboard from '../../../utils/api/dashboard';

// Define the type for card data

interface CardData {
  count: string | number;
  title: string;
  subTitle?: string;
  icon: React.ReactNode;
  trend: number;
  trendType: 'up' | 'down';
  iconBgColor: string;
  trendBgColor: string;
  trendColor: string;
  isLoading: boolean;
}

interface DashboardCardsProperties {
  dateDuration: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * Dashboard cards component with background scrolling image
 */
function DashboardCards({ dateDuration, filters }: DashboardCardsProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('dashboard');
  const { brandData } = useBranding();
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [cardsState, setCardsState] = useState<{
    revenue: CardData;
    energy: CardData;
    activeDevices: CardData;
    endConsumers: CardData;
  }>({
    revenue: {
      count: 0,
      title: 'Total Revenue',
      icon: <RevenueIcon />,
      trend: 12.5,
      trendType: 'up',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#E0F2F1',
      trendColor: '#00695C',
      isLoading: true,
    },
    energy: {
      count: 0,
      title: 'Total Energy',
      subTitle: 'KWh',
      icon: <EnergyIcon />,
      trend: 8.2,
      trendType: 'up',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#E0F2F1',
      trendColor: '#00695C',
      isLoading: true,
    },
    activeDevices: {
      count: 0,
      title: 'Active Devices',
      icon: <ActiveDevicesIcon />,
      trend: 4.1,
      trendType: 'up',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#E0F2F1',
      trendColor: '#00695C',
      isLoading: true,
    },
    endConsumers: {
      count: 0,
      title: 'Total End Consumers',
      icon: <MultiUsersIcon />,
      trend: 0.5,
      trendType: 'down',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#FFEBEE',
      trendColor: '#C62828',
      isLoading: true,
    },
  });

  /**
   * Fetches data for dashboard cards
   */
  const fetchCardData = async (timePeriod: string, hardRefresh = true) => {
    console.log('filters', filters);
    try {
      if (hardRefresh) setIsLoading(true);
      const [revenueResponse] = await Promise.allSettled([
        dashboard.fetchDashboardCardDetails({
          timePeriod,
          type: filters?.type,
          accountCustomId: filters?.facilityIds,
          stationIds: filters?.stationIds,
          deviceIds: filters?.deviceIds,
          hardRefresh,
        }),
      ]);

      setCardsState((previousState) => {
        const newState = { ...previousState };
        // Revenue & Energy
        if (revenueResponse.status === 'fulfilled' && revenueResponse.value.status === 200) {
          const energy = revenueResponse?.value?.data?.finalResult?.electricity?.total || 0;
          newState.revenue.count = revenueResponse?.value?.data?.finalResult?.revenue?.total || 0;
          newState.revenue.subTitle =
            revenueResponse?.value?.data?.finalResult?.revenue?.currency || '';
          newState.energy.count = (energy / 1000).toFixed(2) || 0;
          newState.endConsumers.count =
            revenueResponse?.value?.data?.finalResult?.users?.total || 0;
          newState.activeDevices.count =
            revenueResponse?.value?.data?.finalResult?.electricity?.activeDevices || 0;
          setLastUpdatedTime(
            revenueResponse?.value?.data?.last_updated_time || new Date().toString(),
          );
        }
        newState.revenue.isLoading = false;
        newState.energy.isLoading = false;
        newState.endConsumers.isLoading = false;
        newState.activeDevices.isLoading = false;

        return newState;
      });
    } catch (error) {
      console.error('Error fetching dashboard cards data', error);
      setCardsState((previousState) => ({
        ...previousState,
        revenue: { ...previousState.revenue, isLoading: false },
        energy: { ...previousState.energy, isLoading: false },
        activeDevices: { ...previousState.activeDevices, isLoading: false },
        endConsumers: { ...previousState.endConsumers, isLoading: false },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Manually refreshes the dashboard cards data
   */
  const handleManualRefresh = () => {
    setCardsState((previous) => ({
      revenue: { ...previous.revenue, isLoading: true },
      energy: { ...previous.energy, isLoading: true },
      activeDevices: { ...previous.activeDevices, isLoading: true },
      endConsumers: { ...previous.endConsumers, isLoading: true },
    }));
    fetchCardData(dateDuration, true);
  };

  useEffect(() => {
    fetchCardData(dateDuration, false);
  }, [dateDuration, filters]);

  useEffect(() => {
    setCardsState((previous) => ({
      ...previous,
      revenue: {
        ...previous.revenue,
        subTitle: branding.text.currency,
      },
    }));
  }, [branding.text.currency]);

  /**
   * Renders a single dashboard card
   * @param card - The card data to render
   */
  const renderCard = (card: CardData) => (
    <div className="new-dashboard-card">
      <div className="top-section">
        <h1 className="count">
          {card.isLoading ? '...' : Number(card.count).toLocaleString()}

          {card.title === 'Total Revenue' ? (
            <span className="subtitle">{branding.text.currency || 'SAR'}</span>
          ) : (
            card.subTitle && <span className="subtitle">{card.subTitle}</span>
          )}
        </h1>
        <div className="icon-wrapper">{card.icon}</div>
      </div>
      <div className="bottom-section">
        <p className="title">{card.title}</p>
        <div
          className="trend-wrapper"
          style={{ backgroundColor: card.trendBgColor, display: 'none' }}>
          {card.trendType === 'up' ? <TrendUpIcon /> : <TrendDownIcon />}
          <span style={{ color: card.trendColor }}>{card.trend}%</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-cards-outer">
      <div className="refresh-section">
        <div className="refresh-icon">
          {isLoading ? (
            <div className="loading">
              <LoaderIcon style={{ width: '20px', height: '20px' }} />
            </div>
          ) : (
            <MyButton
              label=""
              buttonType="none"
              onClickFunc={handleManualRefresh}
              svgBackIcon={<RefreshIcon style={{ width: '20px', height: '20px' }} />}
            />
          )}
        </div>
        <p className="updated-time">
          {t('lastUpdatedTime')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}{' '}
        </p>
      </div>
      <div className="dashboard-cards-wrap-new">
        {renderCard(cardsState.revenue)}
        {renderCard(cardsState.energy)}
        {renderCard(cardsState.activeDevices)}
        {permissionCheck('end_consumer_management') &&
          (!brandData?.useSharedDatabase as any) &&
          renderCard(cardsState.endConsumers)}
      </div>
    </div>
  );
}

DashboardCards.defaultProps = {
  filters: undefined,
};

export default DashboardCards;
